import React, { useEffect, useState } from "react";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useCenter } from "@/context/CenterContext";
import UserAvatar from "@/components/UserAvatar";
import UploadButton, { uploadFile } from "@/components/UploadButton";
import EmployeeDetail from "@/components/EmployeeDetail";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, Trash2, Phone, Mail, FileText, X, Download, MoreHorizontal, Pencil } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", first_name: "", last_name: "", email: "", password: "", role: "employee", position: "", phone: "",
  date_of_birth: "", date_of_hire: "", nas: "", address: "", employment_status: "full_time",
  profile_picture: null, emergency_contacts: [], certifications: [], notes: "", active: true, documents: [],
};

function EmployeeDialog({ open, onOpenChange, employee, onSaved }) {
  const { t } = useT();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!employee;

  useEffect(() => {
    if (open) setForm(employee ? { ...EMPTY, ...employee, password: "" } : EMPTY);
  }, [open, employee]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit && !payload.password) delete payload.password;
      if (isEdit) await api.put(`/employees/${employee.id}`, payload);
      else await api.post("/employees", payload);
      toast.success(t("save"));
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addCert = () => set("certifications", [...(form.certifications || []), { name: "", expiry_date: "" }]);
  const addContact = () => set("emergency_contacts", [...(form.emergency_contacts || []), { name: "", relationship: "", phone: "" }]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle>{isEdit ? t("editEmployee") : t("addEmployee")}</DialogTitle></DialogHeader>
        <div className="flex items-center gap-4 mb-2">
          <UserAvatar name={`${form.first_name || ""} ${form.last_name || ""}`.trim() || form.name} src={form.profile_picture} size={64} />
          <UploadButton label={t("profilePicture")} accept="image/*" category="avatar" testId="upload-avatar"
            onUploaded={(doc) => set("profile_picture", `file:${doc.id}`)} />
        </div>
        <Tabs defaultValue="info">
          <TabsList className="grid grid-cols-3">
            <TabsTrigger value="info" data-testid="tab-info">{t("profile")}</TabsTrigger>
            <TabsTrigger value="certs" data-testid="tab-certs">{t("certifications")}</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">{t("emergencyContacts")}</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("firstName")}><Input value={form.first_name || ""} onChange={(e) => set("first_name", e.target.value)} data-testid="emp-first-name" /></Field>
              <Field label={t("lastName")}><Input value={form.last_name || ""} onChange={(e) => set("last_name", e.target.value)} data-testid="emp-last-name" /></Field>
              <Field label={t("email")}><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={isEdit} data-testid="emp-email" /></Field>
              <Field label={isEdit ? t("newPassword") : t("password")}><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder={isEdit ? "••••" : ""} data-testid="emp-password" /></Field>
              <Field label={t("role")}>
                <Select value={form.role} onValueChange={(v) => set("role", v)}>
                  <SelectTrigger data-testid="emp-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">{t("employee")}</SelectItem>
                    <SelectItem value="supervisor">{t("supervisor")}</SelectItem>
                    <SelectItem value="admin">{t("admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("position")}><Input value={form.position || ""} onChange={(e) => set("position", e.target.value)} data-testid="emp-position" /></Field>
              <Field label={t("phone")}><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
              <Field label={t("dateOfBirth")}><Input type="date" value={form.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
              <Field label={t("dateOfHire")}><Input type="date" value={form.date_of_hire || ""} onChange={(e) => set("date_of_hire", e.target.value)} /></Field>
              <Field label={t("nas")}><Input value={form.nas || ""} onChange={(e) => set("nas", e.target.value)} /></Field>
              <Field label={t("employmentStatus")}>
                <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">{t("fullTime")}</SelectItem>
                    <SelectItem value="part_time">{t("partTime")}</SelectItem>
                    <SelectItem value="casual">{t("casual")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label={t("address")}><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label={t("notes")}><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></Field>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="active" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4" data-testid="emp-active" />
              <Label htmlFor="active">{t("active")}</Label>
            </div>
          </TabsContent>

          <TabsContent value="certs" className="space-y-3 pt-2">
            {(form.certifications || []).map((c, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Field label={t("certName")} className="flex-1"><Input value={c.name} onChange={(e) => { const cs = [...form.certifications]; cs[i].name = e.target.value; set("certifications", cs); }} /></Field>
                <Field label={t("expiryDate")}><Input type="date" value={c.expiry_date || ""} onChange={(e) => { const cs = [...form.certifications]; cs[i].expiry_date = e.target.value; set("certifications", cs); }} /></Field>
                <Button variant="ghost" size="icon" onClick={() => set("certifications", form.certifications.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addCert} data-testid="add-cert"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          </TabsContent>

          <TabsContent value="contacts" className="space-y-3 pt-2">
            {(form.emergency_contacts || []).map((c, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Field label={t("contactName")} className="flex-1"><Input value={c.name} onChange={(e) => { const cs = [...form.emergency_contacts]; cs[i].name = e.target.value; set("emergency_contacts", cs); }} /></Field>
                <Field label={t("relationship")}><Input value={c.relationship || ""} onChange={(e) => { const cs = [...form.emergency_contacts]; cs[i].relationship = e.target.value; set("emergency_contacts", cs); }} /></Field>
                <Field label={t("phone")}><Input value={c.phone || ""} onChange={(e) => { const cs = [...form.emergency_contacts]; cs[i].phone = e.target.value; set("emergency_contacts", cs); }} /></Field>
                <Button variant="ghost" size="icon" onClick={() => set("emergency_contacts", form.emergency_contacts.filter((_, j) => j !== i))}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addContact} data-testid="add-contact"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={save} disabled={saving} data-testid="save-employee">{t("save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;
}

export default function Employees() {
  const { t } = useT();
  const { user } = useAuth();
  const { isManager, role, isSuperAdmin } = useCenter();
  const [employees, setEmployees] = useState([]);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = () => api.get("/employees").then((r) => setEmployees(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm(t("delete") + "?")) return;
    try { await api.delete(`/employees/${id}`); toast.success(t("delete")); setSelectedId(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = employees.filter((e) => e.name?.toLowerCase().includes(q.toLowerCase()) || e.position?.toLowerCase().includes(q.toLowerCase()));
  const selected = employees.find((e) => e.id === selectedId) || null;

  return (
    <div className="h-full flex overflow-hidden">
      {/* Master list */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-border flex flex-col shrink-0 ${selected ? "hidden md:flex" : "flex"}`}>
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight">{t("employees")}</h1>
            {isManager && <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="add-employee-button"><Plus className="w-4 h-4 mr-1" />{t("add")}</Button>}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="employee-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-border">
          {filtered.map((e) => (
            <button key={e.id} onClick={() => setSelectedId(e.id)} data-testid="employee-row"
              className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors ${selectedId === e.id ? "bg-primary/10" : "hover:bg-accent/50"}`}>
              <UserAvatar name={e.name} src={e.profile_picture} size={40} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{e.name}</p>
                  {!e.active && <Badge variant="secondary" className="text-[9px] shrink-0">{t("inactive")}</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{e.position || t(e.role)}</p>
              </div>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-center text-muted-foreground py-12 text-sm">{t("noData")}</p>}
        </div>
      </div>

      {/* Detail */}
      <div className={`flex-1 overflow-y-auto scrollbar-thin bg-background ${selected ? "block" : "hidden md:block"}`}>
        {selected ? (
          <EmployeeDetail employee={selected} isManager={isManager} isAdmin={role === "admin" || isSuperAdmin}
            canDelete={(role === "admin" || isSuperAdmin) && selected.id !== user.id}
            onSaved={load} onDelete={del} onBack={() => setSelectedId(null)} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center"><Mail className="w-6 h-6" /></div>
            <p className="text-sm">{t("selectConversation") || "Select"}</p>
          </div>
        )}
      </div>

      <EmployeeDialog open={dialogOpen} onOpenChange={setDialogOpen} employee={null} onSaved={load} />
    </div>
  );
}
